import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { usePagination, SearchBar } from '../components/Pagination';
import { exportElevesCSV, exportElevesPDF } from '../utils/export.helpers';
import { confirmDialog } from '../utils/confirm';

const STATUS = {
  INSCRIT:    ['green', 'Inscrit'],
  PRE_INSCRIT:['blue',  'Pré-inscrit'],
  TRANSFERE:  ['gray',  'Transféré'],
  RADIE:      ['red',   'Radié'],
  DIPLOME:    ['gold',  'Diplômé'],
};

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const ELECTROPHORESIS = ['AA', 'AS', 'SS', 'AC', 'SC'];
const RELATIONSHIPS = ['Père', 'Mère', 'Tuteur', 'Tutrice', 'Grand-père', 'Grand-mère', 'Autre'];
const PROFESSIONS = [
  'Agriculteur(e)', 'Artisan(e)', 'Avocat(e)', 'Chauffeur', 'Commerçant(e)',
  'Comptable', 'Directeur(rice)', 'Electricien(ne)', 'Enseignant(e)', 'Fonctionnaire',
  'Infirmier(ère)', 'Ingénieur(e)', 'Journaliste', 'Magistrat(e)', 'Médecin',
  'Militaire', 'Ouvrier(ère)', 'Pharmacien(ne)', 'Policier(ère)', 'Retraité(e)',
  'Sage-femme', 'Secrétaire', 'Technicien(ne)', 'Sans emploi', 'Autre',
];

const EMPTY = {
  matricule: '', firstName: '', lastName: '', gender: 'M',
  birthDate: '', birthPlace: '', motherTongue: '', originSchool: '',
  bloodGroup: '', phenotype: '',
};

function fmt(n) { return new Intl.NumberFormat('fr-FR').format(n ?? 0) + ' F'; }
function isScolarite(fee) { return fee.name?.toLowerCase().includes('scolarit'); }

export default function Students() {
  const { user: authUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teacherClassIds, setTeacherClassIds] = useState(null); // null = pas de restriction
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [importModal, setImportModal] = useState(false);

  function load() {
    setLoading(true);
    const qs = [];
    if (search.trim()) qs.push(`search=${encodeURIComponent(search.trim())}`);
    api.get(`/students${qs.length ? `?${qs.join('&')}` : ''}`)
      .then(setRows).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }

  useEffect(() => {
    api.get('/catalog/classes').then(setClasses).catch(() => {});
  }, []);

  // Restriction enseignant : récupérer ses classes via l'EDT
  useEffect(() => {
    if (!authUser) return;
    const roles = authUser?.roles || [];
    const isEnseignant = roles.includes('ENSEIGNANT') &&
      !roles.includes('ADMINISTRATEUR') && !roles.includes('PROVISEUR') &&
      !roles.includes('CENSEUR') && !roles.includes('SECRETAIRE') && !roles.includes('COMPTABLE');
    if (!isEnseignant) return;
    const ln = (authUser.lastName || '').trim().toLowerCase();
    const fn = (authUser.firstName || '').trim().toLowerCase();
    Promise.all([api.get('/catalog/teachers'), api.get('/timetable')]).then(([teachers, sessions]) => {
      const me = (teachers || []).find((t) =>
        (t.lastName || '').trim().toLowerCase() === ln &&
        (t.firstName || '').trim().toLowerCase() === fn
      );
      if (!me) return;
      const ids = new Set(
        (sessions || []).filter((s) => s.teacher?.id === me.id).map((s) => s.class?.id).filter(Boolean)
      );
      setTeacherClassIds(ids);
    }).catch(() => {});
  }, [authUser]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]);

  async function archive(id) {
    if (!(await confirmDialog({ title: 'Radier l\'élève', message: 'Radier cet élève ? Action irréversible.', confirmLabel: 'Radier', danger: true }))) return;
    try { await api.del(`/students/${id}`); load(); } catch (e) { setError(e.message); }
  }

  async function deleteStudent(id) {
    if (!(await confirmDialog({ title: 'Supprimer l\'élève', message: 'Supprimer définitivement cet élève ? Cette action est irréversible.', confirmLabel: 'Supprimer', danger: true }))) return;
    try { await api.del(`/students/${id}/permanent`).catch(() => api.del(`/students/${id}`)); load(); } catch (e) { setError(e.message); }
  }

  async function deleteSelected() {
    if (selected.size === 0) return;
    if (!(await confirmDialog({ title: `Supprimer ${selected.size} élève(s)`, message: `Supprimer définitivement ${selected.size} élève(s) sélectionné(s) ? Cette action est irréversible.`, confirmLabel: 'Supprimer', danger: true }))) return;
    try {
      await Promise.all([...selected].map((id) => api.del(`/students/${id}/permanent`).catch(() => api.del(`/students/${id}`))));
      setSelected(new Set());
      load();
    } catch (e) { setError(e.message); }
  }

  function toggleSelect(id) {
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function toggleSelectAll() {
    setSelected((prev) => prev.size === filtered.length ? new Set() : new Set(filtered.map((s) => s.id)));
  }

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const filtered = rows.filter((s) => {
    const cls = s.enrollments?.[0]?.class?.id || '';
    const matchC = !classFilter || cls === classFilter;
    const matchS = !statusFilter || s.status === statusFilter;
    const matchTeacher = !teacherClassIds || teacherClassIds.has(cls);
    return matchC && matchS && matchTeacher;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset page quand filtre change
  useEffect(() => setPage(1), [search, classFilter, statusFilter]);

  const stats = {
    total: rows.length,
    garcons: rows.filter((s) => s.gender === 'M').length,
    filles: rows.filter((s) => s.gender === 'F').length,
    inscrits: rows.filter((s) => s.status === 'INSCRIT').length,
  };

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi navy"><span>Total élèves</span><strong>{stats.total}</strong></div>
        <div className="kpi"><span>Garçons</span><strong>{stats.garcons}</strong></div>
        <div className="kpi"><span>Filles</span><strong>{stats.filles}</strong></div>
        <div className="kpi green"><span>Inscrits</span><strong>{stats.inscrits}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Registre des élèves</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input className="search-input" placeholder="Nom, matricule…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} />
            <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} style={{ width: 160 }}>
              <option value="">Toutes les classes</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 140 }}>
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS).map(([v, [, l]]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {selected.size > 0 && (
              <button className="btn-sm" style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 600 }} onClick={deleteSelected}>
                Supprimer {selected.size} sélectionné(s)
              </button>
            )}
            <button className="btn-ghost btn-sm" onClick={() => setImportModal(true)}>↑ Importer liste</button>
            <button className="btn-ghost btn-sm" onClick={() => exportElevesCSV(filtered)}>↓ Excel</button>
            <button className="btn-ghost btn-sm" onClick={() => exportElevesPDF(filtered)}>↓ PDF</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {loading ? <div className="loading">Chargement…</div> : filtered.length === 0 ? (
            <div className="empty">Aucun élève trouvé.</div>
          ) : (
            <div className="table-wrap" style={{ fontSize: 13 }}><style dangerouslySetInnerHTML={{ __html: '.students-table td, .students-table th { padding-top: 5px !important; padding-bottom: 5px !important; }' }} /><table className="students-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <span onClick={toggleSelectAll} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, border: `1.5px solid ${selected.size === filtered.length && filtered.length > 0 ? 'var(--primary,#2563eb)' : '#94a3b8'}`, borderRadius: 3, background: selected.size === filtered.length && filtered.length > 0 ? 'var(--primary,#2563eb)' : '#fff', cursor: 'pointer' }}>
                      {selected.size === filtered.length && filtered.length > 0 && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1.5 4L3.8 6.3L8.5 1.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </span>
                  </th>
                  <th>Matricule</th><th>Nom & Prénom</th><th>Sexe</th>
                  <th>Né(e) le</th><th>Classe</th><th>Groupe sang.</th>
                  <th>Électrophorèse</th><th>Statut</th><th></th>
                </tr>
              </thead>
              <tbody>{paginated.map((s) => {
                const cls = s.enrollments?.[0]?.class?.name || '—';
                const [badge, label] = STATUS[s.status] || ['gray', s.status];
                const isSel = selected.has(s.id);
                return (
                  <tr key={s.id} style={{ background: isSel ? 'rgba(37,99,235,0.05)' : undefined, lineHeight: 1 }}>
                    <td>
                      <span onClick={() => toggleSelect(s.id)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, border: `1.5px solid ${isSel ? 'var(--primary,#2563eb)' : '#94a3b8'}`, borderRadius: 3, background: isSel ? 'var(--primary,#2563eb)' : '#fff', cursor: 'pointer' }}>
                        {isSel && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1.5 4L3.8 6.3L8.5 1.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </span>
                    </td>
                    <td className="mono">{s.matricule}</td>
                    <td><b>{s.lastName} {s.firstName}</b></td>
                    <td>{s.gender === 'M' ? '♂ Garçon' : '♀ Fille'}</td>
                    <td className="mono">{s.birthDate ? new Date(s.birthDate).toLocaleDateString('fr-FR') : '—'}</td>
                    <td>{cls}</td>
                    <td>{s.bloodGroup || '—'}</td>
                    <td>{s.phenotype || '—'}</td>
                    <td><span className={`badge ${badge}`}>{label}</span></td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn-ghost btn-sm" onClick={() => setView(s)}>Fiche</button>
                      <button className="btn-ghost btn-sm" onClick={() => setModal(s)}>Modifier</button>
                      <button className="link-danger" style={{ fontSize: '0.75rem' }} onClick={() => archive(s.id)}>Radier</button>
                      <button className="link-danger" style={{ fontSize: '0.75rem', marginLeft: 6 }} onClick={() => deleteStudent(s.id)}>Supprimer</button>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          )}
          {!loading && filtered.length > PAGE_SIZE && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0 2px', fontSize: 13 }}>
              <span style={{ color: 'var(--muted)' }}>
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} sur {filtered.length}
              </span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button className="btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(1)}>«</button>
                <button className="btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => p === '…'
                    ? <span key={`e${i}`} style={{ padding: '0 4px', color: 'var(--muted)' }}>…</span>
                    : <button key={p} className="btn-ghost btn-sm" onClick={() => setPage(p)} style={{ fontWeight: p === page ? 700 : 400, background: p === page ? 'var(--primary-soft,#eff6ff)' : undefined }}>{p}</button>
                  )}
                <button className="btn-ghost btn-sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
                <button className="btn-ghost btn-sm" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {modal && <StudentModal initial={modal} classes={classes} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {view && <StudentView student={view} onClose={() => setView(null)} onEdit={() => { setModal(view); setView(null); }} />}
      {importModal && <ImportModal classes={classes} onClose={() => setImportModal(false)} onDone={() => { setImportModal(false); load(); }} />}
    </>
  );
}

function StudentView({ student: s, onClose, onEdit }) {
  const [parents, setParents] = useState([]);
  const [enrollModal, setEnrollModal] = useState(false);
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    api.get(`/students/${s.id}`).then((full) => setParents(full.parents || [])).catch(() => {});
    api.get('/catalog/classes').then(setClasses).catch(() => {});
  }, [s.id]);

  const cls = s.enrollments?.[0]?.class?.name || '—';
  const [badge, label] = STATUS[s.status] || ['gray', s.status];
  const d = (v) => v ? new Date(v).toLocaleDateString('fr-FR') : '—';
  const R = (l, v) => v != null && v !== '' ? (
    <div className="view-row"><span className="view-label">{l}</span><span className="view-value">{String(v)}</span></div>
  ) : null;

  const sch = s.extra?.scholarship;

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0 }}>{s.lastName} {s.firstName}</h2>
            <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
              <span className="mono" style={{ color: 'var(--muted)' }}>{s.matricule}</span>
              <span className={`badge ${badge}`}>{label}</span>
              <span>{cls}</span>
              {sch?.active && <span className="badge gold">Boursier</span>}
            </div>
          </div>
        </div>

        <div className="fieldset-title">Identité</div>
        <div className="view-grid">
          {R('Sexe', s.gender === 'M' ? 'Masculin' : 'Féminin')}
          {R('Date de naissance', d(s.birthDate))}
          {R('Lieu de naissance', s.birthPlace)}
          {R('Langue maternelle', s.motherTongue)}
          {R('École d\'origine', s.originSchool)}
          {R('Groupe sanguin', s.bloodGroup)}
          {R('Électrophorèse', s.phenotype)}
        </div>

        <div className="fieldset-title">Scolarité</div>
        <div className="view-grid">
          {s.enrollments?.length > 0 ? s.enrollments.map((e) => (
            <div key={e.id} className="view-row">
              <span className="view-label">Classe {e.academicYear?.name || ''}</span>
              <span className="view-value"><b>{e.class?.name || '—'}</b></span>
            </div>
          )) : (
            <div className="view-row"><span className="view-label">Classe</span><span className="view-value">Non inscrit</span></div>
          )}
          {sch?.active && (
            <div className="view-row">
              <span className="view-label">Bourse scolaire</span>
              <span className="view-value">
                {sch.type === 'PERCENTAGE' ? `${sch.value}%` : `${Number(sch.value).toLocaleString('fr-FR')} XOF`} de réduction (scolarité / mensualité)
              </span>
            </div>
          )}
        </div>

        <div className="fieldset-title">Parents & tuteurs</div>
        {parents.length === 0 ? (
          <div className="empty" style={{ marginBottom: 8 }}>Aucun parent enregistré.</div>
        ) : (
          <div className="view-grid">
            {parents.map((p, i) => (
              <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 6, padding: '8px 12px' }}>
                <div style={{ fontWeight: 600 }}>{p.parent?.lastName} {p.parent?.firstName} <small style={{ color: 'var(--muted)' }}>({p.relationship})</small></div>
                {p.parent?.phone && <div style={{ fontSize: '0.82rem' }}>📞 {p.parent.phone}</div>}
                {p.parent?.whatsapp && <div style={{ fontSize: '0.82rem' }}>💬 {p.parent.whatsapp}</div>}
                {p.parent?.email && <div style={{ fontSize: '0.82rem' }}>✉ {p.parent.email}</div>}
                {p.parent?.profession && <div style={{ fontSize: '0.82rem' }}>💼 {p.parent.profession}</div>}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn" onClick={onEdit} style={{ width: 'auto', padding: '0 22px' }}>Modifier</button>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

function StudentModal({ initial, classes, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [tab, setTab] = useState('identite');
  const [f, setF] = useState({
    ...EMPTY,
    matricule:    initial.matricule    || '',
    firstName:    initial.firstName    || '',
    lastName:     initial.lastName     || '',
    gender:       initial.gender       || 'M',
    birthDate:    initial.birthDate    ? String(initial.birthDate).slice(0, 10) : '',
    birthPlace:   initial.birthPlace   || '',
    bloodGroup:   initial.bloodGroup   || '',
    phenotype:    initial.phenotype    || '',
    motherTongue: initial.motherTongue || '',
    originSchool: initial.originSchool || '',
  });
  const [parents, setParents] = useState([{ firstName: '', lastName: '', relationship: 'Père', phone: '', whatsapp: '', email: '', profession: '', isPrimary: true }]);
  const [enroll, setEnroll] = useState({ classId: '', academicYearId: '' });
  const [years, setYears] = useState([]);

  // — Frais —
  const [classFees, setClassFees]       = useState([]);
  const [selFeeIds, setSelFeeIds]       = useState(new Set());
  const [bourseOn, setBourseOn]         = useState(false);
  const [bourseTarget, setBourseTarget] = useState('total');
  const [bourseKind, setBourseKind]     = useState('percent');
  const [bourseAmt, setBourseAmt]       = useState('');

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    api.get('/catalog/years').then((p) => {
      setYears(p || []);
      if (p?.[0]) setEnroll((e) => ({ ...e, academicYearId: e.academicYearId || p[0].id }));
    }).catch(() => {});
    if (isEdit && initial.id) {
      api.get(`/students/${initial.id}`).then((full) => {
        if (full.parents?.length > 0) {
          setParents(full.parents.map((p) => ({
            _parentId: p.parent?.id || null,
            firstName: p.parent?.firstName || '', lastName: p.parent?.lastName || '',
            relationship: p.relationship || 'Père', phone: p.parent?.phone || '',
            whatsapp: p.parent?.whatsapp || '', email: p.parent?.email || '',
            profession: p.parent?.profession || '', isPrimary: p.isPrimaryContact || false,
          })));
        }
        const e = full.enrollments?.[0];
        if (e) setEnroll((prev) => ({ classId: e.classId || prev.classId, academicYearId: e.academicYearId || prev.academicYearId }));
      }).catch(() => {});
    }
  }, []);

  // Charger les frais quand la classe change
  useEffect(() => {
    const cid = enroll.classId;
    if (!cid) { setClassFees([]); setSelFeeIds(new Set()); return; }
    api.get(`/finance/fee-types?classId=${cid}`)
      .catch(() => api.get('/finance/fee-types'))
      .then(async (d) => {
        const fees = Array.isArray(d) ? d : [];
        setClassFees(fees);
        if (isEdit && initial.id) {
          try {
            const invoices = await api.get(`/finance/invoices?studentId=${initial.id}`).catch(() => []);
            const invoicedIds = new Set((Array.isArray(invoices) ? invoices : []).map((inv) => inv.feeTypeId).filter(Boolean));
            setSelFeeIds(new Set(fees.filter((f) => !isScolarite(f) && invoicedIds.has(f.id)).map((f) => f.id)));
          } catch { setSelFeeIds(new Set()); }
        } else {
          setSelFeeIds(new Set());
        }
      })
      .catch(() => setClassFees([]));
  }, [enroll.classId]);

  function toggleFee(id) {
    setSelFeeIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function setParent(i, k, v) {
    setParents((prev) => prev.map((p, j) => j === i ? { ...p, [k]: v } : p));
  }

  function addParent() {
    setParents((prev) => [...prev, { firstName: '', lastName: '', relationship: 'Mère', phone: '', whatsapp: '', email: '', profession: '', isPrimary: false }]);
  }

  function removeParent(i) {
    setParents((prev) => prev.filter((_, j) => j !== i));
  }

  async function save() {
    if (!f.matricule || !f.firstName || !f.lastName) { setError('Matricule, nom et prénom sont obligatoires.'); return; }
    setSaving(true);
    try {
      const payload = {
        matricule:    f.matricule,
        firstName:    f.firstName,
        lastName:     f.lastName,
        gender:       f.gender,
        birthDate:    f.birthDate || undefined,
        birthPlace:   f.birthPlace || undefined,
        bloodGroup:   f.bloodGroup || undefined,
        phenotype:    f.phenotype || undefined,
        motherTongue: f.motherTongue || undefined,
        originSchool: f.originSchool || undefined,
      };
      let studentId = initial.id;
      if (isEdit) {
        await api.patch(`/students/${initial.id}`, payload);
        for (const p of parents) {
          if (!p.firstName && !p.lastName) continue;
          const { _parentId, ...data } = p;
          if (_parentId) {
            await api.patch(`/students/${studentId}/parents/${_parentId}`, data);
          } else {
            await api.post(`/students/${studentId}/parents`, data);
          }
        }
        // Générer les factures pour les frais sélectionnés (si une classe est choisie)
        if (enroll.classId && classFees.length > 0) {
          try {
            const scFee  = classFees.find(isScolarite);
            const feeIds = [...(scFee ? [scFee.id] : []), ...Array.from(selFeeIds)];
            for (const feeTypeId of feeIds) {
              await api.post('/finance/invoices/generate', { classId: enroll.classId, feeTypeId }).catch(() => {});
            }
          } catch (_) {}
        }
      } else {
        const created = await api.post('/students', payload);
        studentId = created.id;
        if (enroll.classId && enroll.academicYearId) {
          await api.post(`/students/${studentId}/enroll`, enroll).catch(() => {});
        }
        for (const p of parents) {
          if (p.firstName && p.lastName) {
            const { _parentId, ...data } = p;
            await api.post(`/students/${studentId}/parents`, data);
          }
        }
      }
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Modifier l\'élève' : 'Nouvel élève'}</h2>
        {error && <div className="form-error">{error}</div>}

        <div className="tabs" style={{ marginBottom: 12 }}>
          {[['identite', 'Identité'], ['scolarite', 'Scolarité'], ['parents', 'Parents & tuteurs']].map(([v, l]) => (
            <button key={v} className={`tab ${tab === v ? 'active' : ''}`} onClick={() => setTab(v)}>{l}</button>
          ))}
        </div>

        {tab === 'identite' && (
          <>
            <div className="fieldset-title">Identification</div>
            <div className="grid-3">
              <div className="field"><label>Matricule *</label><input value={f.matricule} onChange={set('matricule')} /></div>
              <div className="field"><label>Nom *</label><input value={f.lastName} onChange={set('lastName')} /></div>
              <div className="field"><label>Prénom(s) *</label><input value={f.firstName} onChange={set('firstName')} /></div>
              <div className="field"><label>Sexe *</label>
                <select value={f.gender} onChange={set('gender')}>
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
              </div>
              <div className="field"><label>Date de naissance</label><input type="date" value={f.birthDate} onChange={set('birthDate')} /></div>
              <div className="field"><label>Lieu de naissance</label><input value={f.birthPlace || ''} onChange={set('birthPlace')} /></div>
            </div>
            <div className="fieldset-title">Informations médicales</div>
            <div className="grid-3">
              <div className="field"><label>Groupe sanguin</label>
                <select value={f.bloodGroup || ''} onChange={set('bloodGroup')}>
                  <option value="">—</option>
                  {BLOOD_GROUPS.map((g) => <option key={g}>{g}</option>)}
                </select>
              </div>
              <div className="field"><label>Électrophorèse</label>
                <select value={f.phenotype || ''} onChange={set('phenotype')}>
                  <option value="">—</option>
                  {ELECTROPHORESIS.map((e) => <option key={e}>{e}</option>)}
                </select>
              </div>
            </div>
            <div className="fieldset-title">Origine</div>
            <div className="grid-3">
              <div className="field"><label>Langue maternelle</label><input value={f.motherTongue || ''} onChange={set('motherTongue')} /></div>
              <div className="field"><label>École d'origine</label><input value={f.originSchool || ''} onChange={set('originSchool')} /></div>
            </div>
          </>
        )}

        {tab === 'scolarite' && (
          <>
            <div className="fieldset-title">Inscription</div>
            <div className="grid-3">
              <div className="field"><label>Classe</label>
                <select value={enroll.classId} onChange={(e) => setEnroll((p) => ({ ...p, classId: e.target.value }))}>
                  <option value="">—</option>
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="field"><label>Année académique</label>
                <select value={enroll.academicYearId} onChange={(e) => setEnroll((p) => ({ ...p, academicYearId: e.target.value }))}>
                  <option value="">—</option>
                  {years.map((y) => <option key={y.id} value={y.id}>{y.label}</option>)}
                </select>
              </div>
            </div>
            <FraisSection
              classId={enroll.classId}
              fees={classFees}
              selFeeIds={selFeeIds}
              onToggle={toggleFee}
              bourseOn={bourseOn}         setBourseOn={setBourseOn}
              bourseTarget={bourseTarget} setBourseTarget={setBourseTarget}
              bourseKind={bourseKind}     setBourseKind={setBourseKind}
              bourseAmt={bourseAmt}       setBourseAmt={setBourseAmt}
            />
          </>
        )}

        {tab === 'parents' && (
          <>
            {parents.map((p, i) => (
              <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <b>Parent / Tuteur {i + 1}</b>
                  {parents.length > 1 && <button className="link-danger" style={{ fontSize: '0.75rem' }} onClick={() => removeParent(i)}>Supprimer</button>}
                </div>
                <div className="grid-3">
                  <div className="field"><label>Nom *</label><input value={p.lastName} onChange={(e) => setParent(i, 'lastName', e.target.value)} /></div>
                  <div className="field"><label>Prénom *</label><input value={p.firstName} onChange={(e) => setParent(i, 'firstName', e.target.value)} /></div>
                  <div className="field"><label>Lien</label>
                    <select value={p.relationship} onChange={(e) => setParent(i, 'relationship', e.target.value)}>
                      {RELATIONSHIPS.map((r) => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Téléphone</label><input value={p.phone} onChange={(e) => setParent(i, 'phone', e.target.value)} /></div>
                  <div className="field"><label>WhatsApp</label><input value={p.whatsapp} onChange={(e) => setParent(i, 'whatsapp', e.target.value)} /></div>
                  <div className="field"><label>Email</label><input type="email" value={p.email} onChange={(e) => setParent(i, 'email', e.target.value)} /></div>
                  <div className="field"><label>Profession</label>
                    <select value={p.profession} onChange={(e) => setParent(i, 'profession', e.target.value)}>
                      <option value="">—</option>
                      {PROFESSIONS.map((pr) => <option key={pr}>{pr}</option>)}
                    </select>
                  </div>
                  <div className="field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={p.isPrimary} onChange={(e) => setParent(i, 'isPrimary', e.target.checked)} id={`primary-${i}`} />
                    <label htmlFor={`primary-${i}`}>Contact principal</label>
                  </div>
                </div>
              </div>
            ))}
            <button className="btn-ghost btn-sm" onClick={addParent}>+ Ajouter un parent / tuteur</button>
          </>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : (isEdit ? 'Enregistrer' : 'Créer l\'élève')}</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

// ── Case à cocher custom (immunisée contre le CSS global) ────────────────────
function Tick({ checked, onClick, disabled }) {
  return (
    <span onClick={disabled ? undefined : onClick} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 16, height: 16, flexShrink: 0,
      border: `1.5px solid ${checked ? 'var(--primary,#2563eb)' : '#94a3b8'}`,
      borderRadius: 3,
      background: checked ? 'var(--primary,#2563eb)' : '#fff',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all .12s',
      opacity: disabled ? 0.75 : 1,
    }}>
      {checked && (
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path d="M1.5 4L3.8 6.3L8.5 1.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </span>
  );
}

// ── Composant réutilisable : section frais + bourse ──────────────────────────
function FraisSection({ classId, fees, selFeeIds, onToggle, bourseOn, setBourseOn, bourseTarget, setBourseTarget, bourseKind, setBourseKind, bourseAmt, setBourseAmt }) {
  const scolariteFee = fees.find(isScolarite);
  const optFees      = fees.filter((f) => !isScolarite(f));

  const scolariteAmt  = scolariteFee?.amount ?? 0;
  const optionalTotal = optFees.filter((f) => selFeeIds.has(f.id)).reduce((s, f) => s + (f.amount ?? 0), 0);
  const total         = scolariteAmt + optionalTotal;

  const bourseBase = bourseTarget === 'scolarite' ? scolariteAmt : total;
  const discount   = bourseOn && bourseAmt
    ? bourseKind === 'percent'
      ? Math.round(bourseBase * Math.min(100, Math.max(0, parseFloat(bourseAmt) || 0)) / 100)
      : Math.max(0, parseFloat(bourseAmt) || 0)
    : 0;
  const net = Math.max(0, total - discount);

  if (!classId || fees.length === 0) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <div className="fieldset-title">Frais de scolarité</div>

      {scolariteFee && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', background: 'var(--surface-soft)', border: '1px solid var(--line)', borderRadius: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Tick checked disabled />
            <span style={{ fontWeight: 600, fontSize: 13 }}>{scolariteFee.name}</span>
            <span style={{ fontSize: 10, color: 'var(--muted)', background: 'var(--line-soft)', padding: '1px 8px', borderRadius: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Obligatoire</span>
          </div>
          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--muted)', background: 'var(--line-soft)', padding: '3px 10px', borderRadius: 6, minWidth: 90, textAlign: 'right', display: 'inline-block' }}>
            {fmt(scolariteFee.amount)}
          </span>
        </div>
      )}

      {optFees.map((fee) => {
        const checked = selFeeIds.has(fee.id);
        return (
          <div key={fee.id}
            onClick={() => onToggle(fee.id)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', border: `1px solid ${checked ? 'var(--primary-light,#bfdbfe)' : 'var(--line-soft)'}`, borderRadius: 8, marginBottom: 6, cursor: 'pointer', background: checked ? 'var(--primary-soft,#eff6ff)' : 'transparent', transition: 'background .1s,border-color .1s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Tick checked={checked} onClick={(e) => { e.stopPropagation(); onToggle(fee.id); }} />
              <span style={{ fontSize: 13 }}>{fee.name}</span>
            </div>
            <span style={{ fontWeight: checked ? 700 : 400, fontSize: 13, color: checked ? 'var(--text)' : 'var(--muted)' }}>
              {fmt(fee.amount)}
            </span>
          </div>
        );
      })}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--surface-soft)', border: '1px solid var(--line)', borderRadius: 8, marginTop: 10 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>Total sélectionné</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--primary,#2563eb)' }}>{fmt(total)}</span>
      </div>

      <div onClick={() => setBourseOn((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 14, padding: '10px 14px', border: '1px solid var(--line)', borderRadius: bourseOn ? '8px 8px 0 0' : 8 }}>
        <Tick checked={bourseOn} onClick={(e) => { e.stopPropagation(); setBourseOn((v) => !v); }} />
        <span style={{ fontWeight: 600, fontSize: 13 }}>Appliquer une bourse / réduction</span>
      </div>

      {bourseOn && (
        <div style={{ border: '1px solid var(--line)', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 14 }}>
          <div className="grid-3">
            <div className="field">
              <label>Appliquer sur</label>
              <select value={bourseTarget} onChange={(e) => setBourseTarget(e.target.value)}>
                <option value="total">Total des frais</option>
                <option value="scolarite">Scolarité uniquement</option>
              </select>
            </div>
            <div className="field">
              <label>Type de réduction</label>
              <select value={bourseKind} onChange={(e) => setBourseKind(e.target.value)}>
                <option value="percent">Pourcentage (%)</option>
                <option value="amount">Montant fixe (F)</option>
              </select>
            </div>
            <div className="field">
              <label>{bourseKind === 'percent' ? 'Taux (%)' : 'Montant (F)'}</label>
              <input type="number" min="0" max={bourseKind === 'percent' ? 100 : undefined}
                value={bourseAmt} onChange={(e) => setBourseAmt(e.target.value)}
                placeholder={bourseKind === 'percent' ? 'Ex : 50' : 'Ex : 25000'} />
            </div>
          </div>

          {bourseAmt && (
            <div style={{ background: 'var(--surface-soft)', border: '1px solid var(--line-soft)', borderRadius: 8, padding: '12px 14px', marginTop: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginBottom: 5 }}>
                <span>Montant brut</span><span>{fmt(total)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--success,#16a34a)', marginBottom: 8 }}>
                <span>Bourse ({bourseTarget === 'scolarite' ? 'scolarité' : 'total'}{bourseKind === 'percent' ? ` · ${bourseAmt}%` : ''})</span>
                <span>− {fmt(discount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 14, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
                <span>Net à payer</span>
                <span style={{ color: 'var(--primary,#2563eb)' }}>{fmt(net)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Import liste Excel ──────────────────────────────────────────────────────
function ImportModal({ classes, onClose, onDone }) {
  const [rows, setRows]         = useState([]);
  const [yearId, setYearId]     = useState('');
  const [years, setYears]       = useState([]);
  const [status, setStatus]     = useState('idle');
  const [progress, setProgress] = useState({ done: 0, total: 0, errors: [] });
  const [fileErr, setFileErr]   = useState('');

  useEffect(() => {
    api.get('/catalog/years').then((apiYears) => {
      const apiList = apiYears || [];
      const current = new Date().getFullYear();
      const generated = [];
      for (let y = current + 1; y >= 2020; y--) {
        const label = `${y}-${y + 1}`;
        const found = apiList.find((a) => a.label === label);
        generated.push(found || { id: label, label });
      }
      setYears(generated);
      const active = apiList.find((a) => a.isActive);
      if (active) setYearId(active.id);
      else if (generated[1]) setYearId(generated[1].id);
    }).catch(() => {});
  }, []);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileErr('');
    try {
      const XLSX = await import('xlsx');
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type: 'array' });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const normalized = data.map((r) => {
        const n = {};
        for (const k of Object.keys(r)) n[k.trim().toUpperCase()] = String(r[k]).trim();
        return n;
      }).filter((r) => r.MATRICULE && r.NOM);
      if (normalized.length === 0) { setFileErr('Aucune ligne valide. Vérifiez les colonnes MATRICULE et NOM.'); return; }
      setRows(normalized);
      setStatus('preview');
    } catch (err) {
      setFileErr('Erreur de lecture : ' + err.message);
    }
  }

  async function runImport() {
    setStatus('importing');
    const total  = rows.length;
    const errors = [];
    let done     = 0;

    // Class map: exact + fuzzy par niveau (ex: "6E" → "6e A", "3E" → "3ème A")
    function levelKey(name) {
      const n = (name || '').trim().toUpperCase();
      const m = n.match(/^(\d+|CP\d+|GS|MS|PS|CE\d*|CM\d*)/);
      return m ? m[1] : n;
    }
    const classExact = {};
    const classLevel = {};
    for (const c of classes) {
      const up = (c.name || '').trim().toUpperCase();
      classExact[up] = c.id;
      const lk = levelKey(up);
      if (!classLevel[lk]) classLevel[lk] = c.id;
    }
    function findClassId(name) {
      if (!name) return null;
      const up = (name || '').trim().toUpperCase();
      return classExact[up] || classLevel[levelKey(up)] || null;
    }

    for (const r of rows) {
      try {
        const payload = {
          matricule: r.MATRICULE,
          lastName:  r.NOM,
          firstName: r['PRENOMS'] || r['PRÉNOM'] || r['PRENOM'] || '',
          gender:    'M',
        };
        let studentId;
        try {
          const created = await api.post('/students', payload);
          studentId = created.id;
        } catch {
          // Élève déjà existant — chercher par matricule
          const found = await api.get(`/students?matricule=${encodeURIComponent(r.MATRICULE)}&limit=1`).catch(() => null);
          const s = Array.isArray(found) ? found[0] : found?.data?.[0] || found?.students?.[0];
          studentId = s?.id;
        }
        if (studentId) {
          const classId = findClassId(r.CLASSE);
          if (classId && yearId) {
            await api.post(`/students/${studentId}/enroll`, { classId, academicYearId: yearId }).catch(() => {});
          }
        } else {
          errors.push(`${r.MATRICULE} — introuvable`);
        }
      } catch (err) {
        errors.push(`${r.MATRICULE} — ${err.message}`);
      }
      done++;
      setProgress({ done, total, errors: [...errors] });
    }
    setStatus('done');
  }

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="modal-overlay" onClick={status === 'importing' ? undefined : onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <h2>Importer une liste d'élèves</h2>

        {status === 'idle' && (
          <>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
              Fichier Excel attendu avec les colonnes : <b>MATRICULE, NOM, PRENOMS, CLASSE</b>
            </p>
            <div className="field"><label>Année académique</label>
              <select value={yearId} onChange={(e) => setYearId(e.target.value)}>
                <option value="">—</option>
                {years.map((y) => <option key={y.id} value={y.id}>{y.label}</option>)}
              </select>
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <label>Fichier (.xlsx)</label>
              <input type="file" accept=".xlsx,.xls" onChange={handleFile} />
            </div>
            {fileErr && <div className="form-error">{fileErr}</div>}
          </>
        )}

        {status === 'preview' && (
          <>
            <p style={{ fontSize: 13, marginBottom: 12 }}>
              <b>{rows.length} élève(s)</b> détectés. Aperçu des 5 premiers :
            </p>
            <div className="table-wrap" style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 16 }}>
              <table>
                <thead><tr><th>Matricule</th><th>Nom</th><th>Prénoms</th><th>Classe</th></tr></thead>
                <tbody>
                  {rows.slice(0, 5).map((r, i) => (
                    <tr key={i}>
                      <td className="mono">{r.MATRICULE}</td>
                      <td>{r.NOM}</td>
                      <td>{r['PRENOMS'] || r['PRENOM'] || ''}</td>
                      <td>{r.CLASSE}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!yearId && <div className="form-error" style={{ marginBottom: 8 }}>Sélectionnez une année académique.</div>}
          </>
        )}

        {status === 'importing' && (
          <div style={{ padding: '24px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 13, marginBottom: 10 }}>Importation… {progress.done}/{progress.total}</div>
            <div style={{ background: 'var(--line)', borderRadius: 8, height: 8, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'var(--primary,#2563eb)', transition: 'width .2s' }} />
            </div>
          </div>
        )}

        {status === 'done' && (
          <div style={{ padding: '8px 0' }}>
            <p style={{ color: 'green', fontWeight: 600 }}>✓ {progress.done - progress.errors.length} élève(s) importé(s).</p>
            {progress.errors.length > 0 && (
              <>
                <p style={{ color: '#ef4444', fontWeight: 600, marginTop: 8 }}>{progress.errors.length} erreur(s) :</p>
                <ul style={{ fontSize: 12, maxHeight: 140, overflowY: 'auto', margin: '4px 0 0', paddingLeft: 16 }}>
                  {progress.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          {status === 'done'    && <button className="btn-primary" onClick={onDone}>Fermer</button>}
          {status === 'preview' && (
            <>
              <button className="btn-ghost" onClick={() => setStatus('idle')}>Retour</button>
              <button className="btn-primary" onClick={runImport} disabled={!yearId}>Importer {rows.length} élève(s)</button>
            </>
          )}
          {status === 'idle' && <button className="btn-ghost" onClick={onClose}>Annuler</button>}
        </div>
      </div>
    </div>
  );
}
